import { z } from "zod";

import { api } from "~/api";
import { BookingFlow } from "../booking-flows";
import { createServiceResponseSchema } from "../schemas";
import {
  appointmentTypeSchema,
  dateSlotSchema,
  providerSchema,
  providerWithoutSlotsSchema,
  scheduleVisitResponseSchema,
} from "./schemas";
import { GetAppointmentSlotsParams, PatientVisit } from "./types";

export const getAppointmentSlotsByProvider = async (
  params: GetAppointmentSlotsParams,
  isAdmin?: boolean,
) => {
  const endpoint = isAdmin
    ? "/admin/appointment-slots/providers"
    : "/appointments/slots/providers";

  const response = await api.get(endpoint, {
    params,
    paramsSerializer: {
      encode: (value) => value,
    },
  });

  return createServiceResponseSchema(z.array(providerSchema)).parse(
    response.data,
  ).data;
};
export const getAppointmentSlotsByFirstAvailable = async (
  params: GetAppointmentSlotsParams,
) => {
  const response = await api.get("/appointments/slots/first-available", {
    params,
    paramsSerializer: {
      encode: (value) => value,
    },
  });

  return createServiceResponseSchema(dateSlotSchema).parse(response.data).data;
};

export const scheduleVisit = async (
  bookingFlowId: BookingFlow["id"],
  visit: PatientVisit,
) => {
  const formData = new FormData();

  Object.entries(visit).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }

    if (key === "insuranceCardFront" || key === "insuranceCardBack") {
      if (value instanceof File) {
        formData.append(key, value);
      } else if (typeof value === "string") {
        formData.append(key, value);
      }
    } else if (value === null) {
      // Send empty string for null values to satisfy Laravel's 'present' validation rule
      formData.append(key, "");
    } else {
      formData.append(key, String(value));
    }
  });

  const response = await api.post(`${bookingFlowId}/visit`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return scheduleVisitResponseSchema.parse(response.data.data);
};

export const getProviders = async (
  stateId?: number | null,
  insuranceId?: number | null,
  appointmentType?: string,
) => {
  const response = await api.get("/providers", {
    params: {
      filter: {
        state: stateId,
        insurance: insuranceId,
        appointmentType: appointmentType,
      },
    },
  });

  return createServiceResponseSchema(z.array(providerWithoutSlotsSchema)).parse(
    response.data,
  ).data;
};

export const getAppointmentTypes = async () => {
  const response = await api.get("/appointments/types");

  return createServiceResponseSchema(z.array(appointmentTypeSchema)).parse(
    response.data,
  ).data;
};
